'use client';

import { useQuery } from '@tanstack/react-query';
import { audioStoryKeys } from '../queryKeys';
import { getAudioStoriesByStory } from '../actions/getAudioStoriesByStory';
import type { AudioStory } from '../types';
import { whileWaiting } from '@/shared/hooks/liveRefetch';

/**
 * `awaitingFirst`: a narration was ORDERED and its row has not appeared here yet.
 *
 * The row is created the moment the package's media branch starts, and an SSE
 * event invalidates this key — but a single missed event left the panel saying
 * "queued" for the whole generation while the narration was in fact running (and
 * finished first). While nothing is in the list and something is expected, ask
 * again every few seconds; the polling stops with the first row.
 */
export function useAudioStoriesByStory(
  storyId: string | null | undefined,
  opts?: { awaitingFirst?: boolean },
) {
  return useQuery<AudioStory[]>({
    queryKey: [...audioStoryKeys.all, 'byStory', storyId ?? ''],
    queryFn: () => getAudioStoriesByStory(storyId!),
    enabled: !!storyId,
    // SSE invalidates this key on every audio event; short staleTime only adds
    // redundant focus/mount refetches. 30s matches the global default.
    staleTime: 30_000,
    refetchInterval: (query) =>
      whileWaiting(!!opts?.awaitingFirst && !(query.state.data?.length ?? 0), 4_000),
  });
}
